import { ReactElement, memo, useCallback, useState, useMemo, useEffect } from "react";
import { RowClickedEvent, GridReadyEvent } from "ag-grid-community";
import { useHistory, useLocation } from "react-router-dom";
import { Card, Flex, Text, AgGrid, AgGridReact, Grid, Tabs, Tab } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { useAppContext } from "~/contexts/appContext";
import { useGetFundFamilyCasesQuery } from "~/graphql/supergraphHooks";
import { type OverviewFilters, type OverviewSort } from "~/graphql/supergraphTypes";
import { CasesOverview } from "~/pages/Billing/CasesOverview";
import { BillingClientOverviewExport } from "~/pages/Billing/ClientOverview/BillingClientOverviewExport";
import { OverviewGraphqlDataSource } from "~/pages/Billing/ClientOverview/billingGraphqlDataSource";
import { EntitiesExport } from "~/pages/Billing/ClientOverview/EntitiesExport";
import { useOverviewGridUrlSync } from "~/pages/Billing/ClientOverview/hooks/useOverviewGridUrlSync";
import { RowItem } from "~/pages/Billing/ClientOverview/types";
import { getRowId, updateGridDataSource } from "~/pages/Billing/ClientOverview/utils";
import {
  overviewFiltersToFilterModel,
  overviewSortsToSortModel,
} from "~/pages/Billing/ClientOverview/utils/overviewGridTransforms";
import AsOfDateSelector from "~/pages/Billing/components/AsOfDateSelector";
import { LoadingCellRenderer } from "~/pages/Billing/components/LoadingCellRenderer";
import { MoneyCellRenderer } from "~/pages/Billing/components/MoneyCellRenderer";
import {
  PAGINATION,
  GRID_COLUMN_FILTER_DEBOUNCE_MS,
  ARENA_EDITION_FILTER_VALUES,
  PRODUCT_LINE_FILTER_VALUES,
} from "~/pages/Billing/constants";
import { useBillingDate } from "~/pages/Billing/contexts/BillingDateContext";
import { EntitiesOverview } from "~/pages/Billing/EntitiesOverview";
import { getTabFromUrl, updateTabInUrl } from "~/pages/Billing/utils/urlParameterUtils";
import { SPACING } from "~/styles/variables";
import {
  canTriggerBillingExports,
  hasBillingFARole,
  hasBillingGPRole,
} from "~/utils/userRoles";
import {
  billingClientOverviewCard,
  billingClientOverviewContainer,
  billingClientOverviewHeading,
  casesCounterBadge,
} from "./styles_vanilla.css";

enum BillingTabType {
  Clients = "clients",
  Entities = "entities",
  Cases = "cases",
}

const BILLING_TAB_VALUES: BillingTabType[] = [
  BillingTabType.Clients,
  BillingTabType.Entities,
  BillingTabType.Cases,
];

export type OverviewTableProps = {
  handleOnGridReady?: (event: GridReadyEvent) => void;
  handleRowClicked?: (rowData: RowClickedEvent<RowItem>) => void;
  cacheBlockSize?: number;
  paginationPageSize?: number;
};

export const BillingOverview = memo((): ReactElement => {
  const [gridApi, setGridApi] = useState<AgGrid.GridApi>();
  const history = useHistory();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<BillingTabType>(() =>
    getTabFromUrl(location.search, BILLING_TAB_VALUES, BillingTabType.Clients),
  );

  useEffect(() => {
    const tab = getTabFromUrl(location.search, BILLING_TAB_VALUES, BillingTabType.Clients);
    setActiveTab(tab);
  }, [location.search]);

  const handleTabChange = useCallback(
    (tab: BillingTabType) => {
      setActiveTab(tab);
      updateTabInUrl(history, location, tab);
    },
    [history, location],
  );
  const { asOfDateString } = useBillingDate();
  const isBillingAgGridStaticSetFiltersFlagEnabled = useFeatureFlag(
    "billingAgGridStaticSetFilters",
    false,
  );
  const { user } = useAppContext();
  const canExport = canTriggerBillingExports(user?.roles || []);
  const isBillingEnableFADataFlagEnabled = useFeatureFlag("billingEnableFAData", false);
  const isBillingFAManualTaggingEnabled = useFeatureFlag("billingFAManualTagging", false);
  const hasCasesTabAccess =
    hasBillingFARole(user?.roles || []) || hasBillingGPRole(user?.roles || []);

  const { data: casesCountData } = useGetFundFamilyCasesQuery({
    variables: { offset: 0, limit: 1 },
    fetchPolicy: "cache-and-network",
    skip: !isBillingFAManualTaggingEnabled,
  });

  const casesCount = casesCountData?.getFundFamilyCases?.filteredCount ?? 0;

  const colDefs: AgGridReact.AgGridReactProps["columnDefs"] = [
    {
      field: "client",
      headerName: "Client",
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains"],
        maxNumConditions: 1,
        debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
      },
    },
    {
      field: "clientId",
      headerName: "Client ID",
      filter: "agNumberColumnFilter",
      filterParams: {
        filterOptions: ["equals"],
        maxNumConditions: 1,
        debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
      },
      cellStyle: { textAlign: "right" },
    },
    {
      field: "arenaId",
      headerName: "Arena ID",
      filter: "agNumberColumnFilter",
      filterParams: {
        filterOptions: ["equals"],
        maxNumConditions: 1,
        debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
      },
      cellStyle: { textAlign: "right" },
    },
    {
      field: "csm",
      headerName: "Customer success manager",
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains"],
        maxNumConditions: 1,
        debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
      },
    },
    {
      field: "productLine",
      headerName: "Product Lines",
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains"],
        maxNumConditions: 1,
        debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
      },
      ...(isBillingAgGridStaticSetFiltersFlagEnabled && {
        filter: "agSetColumnFilter",
        filterParams: {
          debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
          values: PRODUCT_LINE_FILTER_VALUES,
        },
      }),
    },
    {
      field: "edition",
      headerName: "Arena Edition",
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: ["contains"],
        maxNumConditions: 1,
        debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
      },
      ...(isBillingAgGridStaticSetFiltersFlagEnabled && {
        filter: "agSetColumnFilter",
        filterParams: {
          debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
          values: ARENA_EDITION_FILTER_VALUES,
        },
      }),
    },
    {
      field: "currentBlendSoftwareEum",
      headerName: "Software EUM (USD)",
      filter: false,
      cellRenderer: MoneyCellRenderer,
      cellStyle: { textAlign: "right" },
    },
    ...(isBillingEnableFADataFlagEnabled
      ? [
          {
            field: "faEumUsd",
            headerName: "FA EUM (USD)",
            filter: false,
            cellRenderer: MoneyCellRenderer,
            cellStyle: { textAlign: "right" },
          },
        ]
      : []),
  ];

  const {
    handleFiltersChange,
    handleSortsChange,
    onGridReady,
    onFirstDataRendered,
    onPaginationChanged,
  } = useOverviewGridUrlSync<OverviewFilters, OverviewSort>({
    filtersToFilterModel: (filters) =>
      overviewFiltersToFilterModel(
        filters,
        colDefs,
        isBillingAgGridStaticSetFiltersFlagEnabled,
      ),
    sortsToSortModel: overviewSortsToSortModel,
  });

  const dataSource = useMemo(() => {
    return new OverviewGraphqlDataSource(
      {
        onFiltersChange: handleFiltersChange,
        onSortsChange: handleSortsChange,
      },
      undefined,
      asOfDateString,
    );
  }, [asOfDateString, handleFiltersChange, handleSortsChange]);

  useEffect(() => {
    if (activeTab === BillingTabType.Cases && !hasCasesTabAccess) {
      handleTabChange(BillingTabType.Clients);
    }
  }, [activeTab, hasCasesTabAccess, handleTabChange]);

  useEffect(() => {
    if (dataSource) {
      dataSource.setAsOfDate(asOfDateString);
      updateGridDataSource(gridApi, dataSource);
    }
  }, [asOfDateString, gridApi, dataSource]);

  const handleOnGridReady = useCallback(
    (event: GridReadyEvent<RowItem>) => {
      setGridApi(event.api);
      onGridReady(event, (gridEvent) => {
        updateGridDataSource(gridEvent.api, dataSource);
      });
    },
    [dataSource, onGridReady],
  );

  const handleRowClicked = useCallback(
    (rowData: RowClickedEvent<RowItem>) => {
      if (rowData.data?.clientId) {
        history.push(`/billing/client/${rowData.data.clientId}`, {
          fromBillingOverview: true,
        });
      }
    },
    [history],
  );

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    suppressCellFocus: true,
    rowHeight: 61,
    domLayout: "normal",
    statusBar: {
      statusPanels: [{ statusPanel: "agTotalRowCountComponent" }],
    },
    rowModelType: "serverSide",
    getRowId: getRowId,
    pagination: true,
    paginationPageSizeSelector: false,
    cacheBlockSize: PAGINATION.OVERVIEW_PAGE_SIZE,
    paginationPageSize: PAGINATION.OVERVIEW_PAGE_SIZE,
    onRowClicked: handleRowClicked,
    onGridReady: handleOnGridReady,
    onFirstDataRendered: onFirstDataRendered,
    onPaginationChanged: onPaginationChanged,
    defaultColDef: {
      menuTabs: ["generalMenuTab", "filterMenuTab"],
    },
    sideBar: {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: false,
            suppressColumnExpandAll: true,
          },
        },
      ],
      position: "right",
    },
  };

  return (
    <Flex flexDirection="column" gap={SPACING.LARGE} className={billingClientOverviewContainer}>
      <Flex justifyContent="space-between" alignItems="center">
        <Text variant="h1">Billing</Text>
        <Flex gap={SPACING.MEDIUM} alignItems="center">
          {activeTab !== BillingTabType.Cases && <AsOfDateSelector />}
          {activeTab === BillingTabType.Clients &&
            isBillingEnableFADataFlagEnabled &&
            canExport && <EntitiesExport gridApi={gridApi} />}
          {activeTab === BillingTabType.Clients && canExport && (
            <BillingClientOverviewExport gridApi={gridApi} />
          )}
        </Flex>
      </Flex>
      {isBillingFAManualTaggingEnabled && (
        <Flex>
          <Tabs hasPaddingInline={false}>
            <Tab
              active={activeTab === BillingTabType.Clients}
              text="Clients"
              onClick={() => handleTabChange(BillingTabType.Clients)}
              data-testid="clients-tab"
            />
            <Tab
              active={activeTab === BillingTabType.Entities}
              text="Entities"
              onClick={() => handleTabChange(BillingTabType.Entities)}
              data-testid="entities-tab"
            />
            {hasCasesTabAccess && (
              <Tab
                active={activeTab === BillingTabType.Cases}
                text="Cases"
                onClick={() => handleTabChange(BillingTabType.Cases)}
                data-testid="cases-tab"
                counterBadgeProps={{
                  count: casesCount,
                  variant: "neutral",
                  className: casesCounterBadge,
                }}
              />
            )}
          </Tabs>
        </Flex>
      )}
      {activeTab === BillingTabType.Clients && (
        <Flex flexGrow={1}>
          <Card className={billingClientOverviewCard}>
            <Text variant="h3" className={billingClientOverviewHeading}>
              Clients
            </Text>
            <Flex flexGrow={1}>
              <Grid
                gridOptions={gridOptions}
                columnDefs={colDefs}
                loadingCellRenderer={LoadingCellRenderer}
              />
            </Flex>
          </Card>
        </Flex>
      )}
      {isBillingFAManualTaggingEnabled && activeTab === BillingTabType.Entities && (
        <EntitiesOverview />
      )}
      {isBillingFAManualTaggingEnabled &&
        activeTab === BillingTabType.Cases &&
        hasCasesTabAccess && <CasesOverview />}
    </Flex>
  );
});

BillingOverview.displayName = "BillingOverview";
